import './Modal.css';

import { useTranslation } from 'react-i18next';
import LoadingIndicator from '../Common/LoadingIndicator';
import { createLevel, updateLevel, deleteLevel, getAllLevels } from '../../api/levels';
import { getLevelDomains, createLevelDomain, deleteLevelDomain } from '../../api/levels';
import { getLevelAdminPermissions, createLevelAdminPermission, deleteLevelAdminPermission } from '../../api/admin_permissions';
import { getAllCategories } from '../../api/categories';
import { useEffect, useState } from 'react';
import { getCurrentUser, getAllUsers } from '../../api/users_institutions';
import { createDashboardHistoryLog } from '../../api/history_logs';
import { getUserLevelProgresses, createUserLevelProgress, updateUserLevelProgress, deleteUserLevelProgress } from '../../api/progresses';

// Sent to the backend as-is; only the displayed label is translated (see modalLevel.domains).
const DOMAIN_OPTIONS = [
    'Preschool',
    'Kindergarten',
    'Elementary School',
    'Middle School',
    'High School',
    'University',
];


function ModalLevel({ Action, selectedCategory, selectedLevel, closeModal}) {
    const { t } = useTranslation();
    const [newModuleIcon, setNewModuleIcon] = useState(null);
    const [newModuleIconPreview, setNewModuleIconPreview] = useState('');
    const [editModuleIcon, setEditModuleIcon] = useState(null);
    const [editModuleIconPreview, setEditModuleIconPreview] = useState('');
    const [removeEditIcon, setRemoveEditIcon] = useState(false);
    const [selectedLevelDomains, setSelectedLevelDomains] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [levelIsDraft, setLevelIsDraft] = useState(false);
    const [draftTargetId, setDraftTargetId] = useState('');
    const [draftTargetOptions, setDraftTargetOptions] = useState([]);
    const [adminUsers, setAdminUsers] = useState([]);
    const [selectedAdminUserIds, setSelectedAdminUserIds] = useState([]);

    useEffect(() => {
        let cancelled = false;

        const loadAdminUsers = async () => {
            try {
                const users = await getAllUsers();
                const staffUsers = Array.isArray(users) ? users.filter((user) => user.is_staff && !user.is_superuser) : [];
                if (!cancelled) {
                    setAdminUsers(staffUsers);
                }
            } catch (error) {
                console.error('Error loading admin users:', error);
                if (!cancelled) {
                    setAdminUsers([]);
                }
            }
        };

        loadAdminUsers();

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;

        const loadSelectedLevelAdminAccess = async () => {
            if (Action !== 'Update' || !selectedLevel?.id) {
                return;
            }

            try {
                const permissions = await getLevelAdminPermissions();
                const userIds = Array.isArray(permissions)
                    ? permissions
                        .filter((permission) => Number(permission.level?.id ?? permission.level) === Number(selectedLevel.id))
                        .map((permission) => permission.user?.id ?? permission.user)
                        .filter((id) => id !== undefined && id !== null)
                    : [];

                if (!cancelled) {
                    setSelectedAdminUserIds(userIds);
                }
            } catch (error) {
                console.error('Error loading level admin access:', error);
                if (!cancelled) {
                    setSelectedAdminUserIds([]);
                }
            }
        };

        loadSelectedLevelAdminAccess();

        return () => {
            cancelled = true;
        };
    }, [Action, selectedLevel]);

    useEffect(() => {
        let cancelled = false;

        const loadDraftTargetOptions = async () => {
            try {
                const [categories, levels] = await Promise.all([getAllCategories(), getAllLevels()]);
                if (cancelled) return;
                const categoriesById = new Map((Array.isArray(categories) ? categories : []).map((category) => [category.id, category]));
                const options = (Array.isArray(levels) ? levels : [])
                    .filter((level) => level.id !== selectedLevel?.id)
                    .map((level) => ({
                        ...level,
                        category_name: categoriesById.get(level.category)?.name || '',
                    }));
                setDraftTargetOptions(options);
            } catch (error) {
                console.error('Error loading draft target options:', error);
                if (!cancelled) {
                    setDraftTargetOptions([]);
                }
            }
        };

        loadDraftTargetOptions();

        return () => {
            cancelled = true;
        };
    }, [selectedLevel]);

    useEffect(() => {
        setNewModuleIcon(null);
        setNewModuleIconPreview('');
        setEditModuleIcon(null);
        setRemoveEditIcon(false);
        setEditModuleIconPreview(selectedLevel?.icon_url || '');
        setSelectedLevelDomains([]);
        setLevelIsDraft(selectedLevel?.is_draft || false);
        setDraftTargetId(selectedLevel?.draft_target != null ? String(selectedLevel.draft_target) : '');
        setSelectedAdminUserIds([]);

        let cancelled = false;

        const loadSelectedLevelDomains = async () => {
            if (Action !== 'Update' || !selectedLevel?.id) {
                return;
            }

            try {
                const levelDomains = await getLevelDomains(selectedLevel.id);
                const domains = Array.isArray(levelDomains)
                    ? levelDomains
                        .filter((domain) => Number(domain.level?.id ?? domain.level) === Number(selectedLevel.id))
                        .map((domain) => domain.domain)
                        .filter(Boolean)
                    : [];

                if (!cancelled) {
                    setSelectedLevelDomains(domains);
                }
            } catch (error) {
                console.error('Error loading level domains:', error);
                if (!cancelled) {
                    setSelectedLevelDomains([]);
                }
            }
        };

        loadSelectedLevelDomains();

        return () => {
            cancelled = true;
        };
    }, [Action, selectedLevel]);

    useEffect(() => {
        if (!newModuleIcon) {
            setNewModuleIconPreview('');
            return;
        }

        const previewUrl = URL.createObjectURL(newModuleIcon);
        setNewModuleIconPreview(previewUrl);

        return () => URL.revokeObjectURL(previewUrl);
    }, [newModuleIcon]);

    useEffect(() => {
        if (!editModuleIcon) {
            if (!removeEditIcon) {
                setEditModuleIconPreview(selectedLevel?.icon_url || '');
            }
            return;
        }

        const previewUrl = URL.createObjectURL(editModuleIcon);
        setEditModuleIconPreview(previewUrl);

        return () => URL.revokeObjectURL(previewUrl);
    }, [editModuleIcon, removeEditIcon, selectedLevel]);

    const handleNewIconChange = (event) => {
        setNewModuleIcon(event.target.files?.[0] || null);
    };

    const handleEditIconChange = (event) => {
        setRemoveEditIcon(false);
        setEditModuleIcon(event.target.files?.[0] || null);
    };

    const renderDraftTargetOptions = () => {
        const groupedByCategory = draftTargetOptions.reduce((groups, level) => {
            const key = level.category_name || 'Uncategorized';
            (groups[key] = groups[key] || []).push(level);
            return groups;
        }, {});

        return Object.entries(groupedByCategory).map(([categoryName, levels]) => (
            <optgroup key={categoryName} label={categoryName}>
                {levels.map((level) => (
                    <option key={level.id} value={String(level.id)}>{level.name}</option>
                ))}
            </optgroup>
        ));
    };

    const toggleAdminAccessBtn = (userId) => {
        setSelectedAdminUserIds((currentIds) =>
            currentIds.includes(userId)
                ? currentIds.filter((currentId) => currentId !== userId)
                : [...currentIds, userId]
        );
    };

    const renderAdminAccessCheckboxes = () => {
        return adminUsers.map((admin) => (
            <button
                key={admin.id}
                type="button"
                className={`access-btn${selectedAdminUserIds.includes(admin.id) ? ' active' : ''}`}
                data-val={admin.username}
                onClick={() => toggleAdminAccessBtn(admin.id)}
            >
                {admin.first_name || admin.last_name ? `${admin.first_name} ${admin.last_name}`.trim() : admin.username}
            </button>
        ));
    };

    const toggleAccessBtn = (domain) => {
        setSelectedLevelDomains((currentDomains) =>
            currentDomains.includes(domain)
                ? currentDomains.filter((currentDomain) => currentDomain !== domain)
                : [...currentDomains, domain]
        );
    };

    const replaceLevelDomains = async (levelId) => {
        if (!levelId) {
            return;
        }

        const existingDomains = await getLevelDomains();
        const domainsForLevel = Array.isArray(existingDomains)
            ? existingDomains.filter((domain) => Number(domain.level?.id ?? domain.level) === Number(levelId))
            : [];

        for (const domain of domainsForLevel) {
            await deleteLevelDomain(domain.id);
        }

        for (const domain of selectedLevelDomains) {
            await createLevelDomain({
                level: levelId,
                domain,
            });
        }
    };

    const replaceLevelAdminPermissions = async (levelId) => {
        if (!levelId) {
            return;
        }

        const existingPermissions = await getLevelAdminPermissions();
        const permissionsForLevel = Array.isArray(existingPermissions)
            ? existingPermissions.filter((permission) => Number(permission.level?.id ?? permission.level) === Number(levelId))
            : [];

        for (const permission of permissionsForLevel) {
            await deleteLevelAdminPermission(permission.id);
        }

        for (const userId of selectedAdminUserIds) {
            await createLevelAdminPermission({
                level: levelId,
                user: userId,
            });
        }
    };

    const handleCreateLevel = async () => {
      if (isSubmitting) return;
      const isDraft = document.getElementById('newModuleIsDraft')?.checked || false;
      const draftTarget = draftTargetId;
      if (isDraft && !draftTarget) {
          alert(t('modalLevel.draftTargetRequired'));
          return;
      }

      setIsSubmitting(true);
      const title = document.getElementById('newModuleTitle').value;
      const desc = document.getElementById('newModuleDesc').value;
      const levelData = {
          name: title,
          description: desc,
          icon: newModuleIcon,
          category: selectedCategory?.id || null,
          is_visible: document.getElementById('newLevelVisibility').checked,
          is_draft: isDraft,
          draft_target: isDraft ? draftTarget : null,
      };

      try {
          const newLevel = await createLevel(levelData);
          await replaceLevelDomains(newLevel?.id);
          await replaceLevelAdminPermissions(newLevel?.id);
          console.log('Level created:', newLevel);

            const logData = {
                user_id: getCurrentUser().id,
                log_title: `Created level: ${newLevel.name}`,
                log_date: new Date().toISOString(),
                log_data: {
                    level_id: newLevel.id,
                    level_name: newLevel.name,
                }
            };
            await createDashboardHistoryLog(logData);

            handleCreateUserLevelProgress(newLevel?.id);
          closeModal();
      } catch (error) {
          console.error('Error creating level:', error);
      } finally {
          setIsSubmitting(false);
      }
  };

  const handleUpdateLevel = async (levelId) => {
      if (isSubmitting) return;
      const isDraft = document.getElementById('editModuleIsDraft')?.checked || false;
      const draftTarget = draftTargetId;
      if (isDraft && !draftTarget) {
          alert(t('modalLevel.draftTargetRequired'));
          return;
      }

      setIsSubmitting(true);
      const title = document.getElementById('editModuleTitle').value;
      const desc = document.getElementById('editModuleDesc').value;
      const is_visible = document.getElementById('editLevelVisibility').checked;
      const levelData = {
          name: title,
          description: desc,
          category: selectedLevel?.category || null,
          is_visible: is_visible,
          is_draft: isDraft,
          draft_target: isDraft ? draftTarget : null,
      };

      if (editModuleIcon) {
          levelData.icon = editModuleIcon;
      } else if (removeEditIcon) {
          levelData.icon = null;
      }

      try {
          const updatedLevel = await updateLevel(levelId, levelData);
          await replaceLevelDomains(levelId);
          await replaceLevelAdminPermissions(levelId);
          console.log('Level updated:', updatedLevel);

            const logData = {
                user_id: getCurrentUser().id,
                log_title: `Updated level: ${updatedLevel.name}`,
                log_date: new Date().toISOString(),
                log_data: {
                    level_id: updatedLevel.id,
                    level_name: updatedLevel.name,
                }
            };
            await createDashboardHistoryLog(logData);
          closeModal();
      } catch (error) {
          console.error('Error updating level:', error);
      } finally {
          setIsSubmitting(false);
      }
  };

  const handleDeleteLevel = async (levelId) => {
      if (isSubmitting) return;
      setIsSubmitting(true);
      try {
          const existingDomains = await getLevelDomains(levelId);
          const domainsForLevel = Array.isArray(existingDomains)
              ? existingDomains.filter((domain) => Number(domain.level?.id ?? domain.level) === Number(levelId))
              : [];

          for (const domain of domainsForLevel) {
              await deleteLevelDomain(domain.id);
          }

          await deleteLevel(levelId);
          console.log('Level deleted:', levelId);

            const logData = {
                user_id: getCurrentUser().id,
                log_title: `Deleted level with ID: ${levelId}`,
                log_date: new Date().toISOString(),
                log_data: {
                    level_id: levelId,
                }
            };
            await createDashboardHistoryLog(logData);
          closeModal();
      } catch (error) {
          console.error('Error deleting level:', error);
      } finally {
          setIsSubmitting(false);
      }

  };

    const handleCreateUserLevelProgress = async (levelId) => {
        try {
        await createUserLevelProgress({ level: levelId, user: getCurrentUser().id, progress: 0 });
        } catch (error) {
        console.error('Error creating user level progress:', error);
        }
    };

    return (
        <>
            <LoadingIndicator active={isSubmitting} label={t('common.loading')} />
            <div className="Modal">
            {Action === 'Add' ? (
                <div className="modal-box">
                    <h2>{t('modalLevel.addNewLevel')}</h2>
                    <input
                        type="text"
                        id="newModuleTitle"
                        placeholder={t('modalLevel.levelNamePlaceholder')}
                    />
                    <textarea
                        id="newModuleDesc"
                        placeholder={t('modalLevel.levelDescPlaceholder')}
                    ></textarea>
                    <div className="toggleVisible" style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '15px 0' }}>
                        <div style={{ fontSize: '0.9rem', fontWeight: '600' }}>{t('common.setVisible')}</div>
                        <label className="switch">
                            <input type="checkbox" id="newLevelVisibility" defaultChecked={selectedLevel?.is_visible}/>
                            <span className="slider" ></span>
                        </label>
                    </div>
                    {selectedCategory?.audience === 'teachers' ? (
                        <>
                            <label className="label-text">{t('modalLevel.targetDomainLabel')}</label>
                            <div className="access-btn-group" id="newModuleAccessGroup">
                                {DOMAIN_OPTIONS.map((domain) => (
                                    <button
                                        key={domain}
                                        type="button"
                                        className={`access-btn${selectedLevelDomains.includes(domain) ? ' active' : ''}`}
                                        data-val={domain}
                                        onClick={() => toggleAccessBtn(domain)}
                                    >
                                        {t(`modalLevel.domains.${domain}`)}
                                    </button>
                                ))}
                            </div>
                        </>
                    ) : (
                        <>
                            <div id="newModuleDraftContainer" style={{ display: 'block', marginBottom: '14px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem', fontWeight: '600', cursor: 'pointer', marginBottom: '8px' }}>
                                    <input type="checkbox" id="newModuleIsDraft" style={{ width: '16px', height: '16px', accentColor: 'var(--accent)' }} onChange={(e) => setLevelIsDraft(e.target.checked)} />
                                    {t('modalLevel.draftCheckboxLabel')}
                                </label>
                                {levelIsDraft && (
                                    <select id="newModuleTargetSelect" className="login-input" value={draftTargetId} onChange={(e) => setDraftTargetId(e.target.value)} style={{ display: 'flex', padding: '8px', overflowY: 'auto', maxHeight: '100px' }}>
                                        <option value="">{t('modalLevel.selectTargetModulePlaceholder')}</option>
                                        {renderDraftTargetOptions()}
                                    </select>
                                )}
                            </div>
                            <div id="newModuleAdminAccessContainer" style={{ display: 'flex', flexDirection: 'column', marginBottom: '14px' }}>
                                <label className="label-text">{t('modalLevel.adminAccessLabel')} <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 'normal' }}>{t('modalLevel.adminAccessHint')}</span></label>
                                <div className="access-btn-group" id="newModuleAdminAccessGroup" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                    {renderAdminAccessCheckboxes()}
                                </div>
                            </div>
                        </>
                    )}

                    <label className="label-text" htmlFor="newModuleImage">{t('modalLevel.moduleCoverageImageLabel')}</label>
                    <input
                        type="file"
                        id="newModuleImage"
                        accept="image/*"
                        className="login-input"
                        style={{ paddingTop: '9px' }}
                        onChange={handleNewIconChange}
                    />
                    {newModuleIconPreview && (
                        <div className="module-image-preview">
                            <img src={newModuleIconPreview} alt="Module preview" />
                        </div>
                    )}
                    <div className="modal-buttons">
                        <button className="btn-primary" onClick={handleCreateLevel} disabled={isSubmitting}>
                            {t('common.create')}
                        </button>
                        <button
                            className="btn-primary btn-secondary"
                            onClick={closeModal}
                        >
                            {t('common.cancel')}
                        </button>
                    </div>
                </div>
            ) : Action === 'Update' ? (
                <div className="modal-box">
                    <h2>{t('modalLevel.editLevel')}</h2>
                    <input
                        type="text"
                        id="editModuleTitle"
                        placeholder={t('modalLevel.levelNameFieldPlaceholder')}
                        defaultValue={selectedLevel?.name || ''}
                    />
                    <textarea
                        id="editModuleDesc"
                        placeholder={t('modalLevel.levelDescFieldPlaceholder')}
                        defaultValue={selectedLevel?.description || ''}
                    ></textarea>
                    <div className="toggleVisible" style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '15px 0' }}>
                        <div style={{ fontSize: '0.9rem', fontWeight: '600' }}>{t('common.setVisible')}</div>
                        <label className="switch">
                            <input type="checkbox" id="editLevelVisibility" defaultChecked={selectedLevel?.is_visible}/>
                            <span className="slider" ></span>
                        </label>
                    </div>
                    {selectedCategory?.audience === 'teachers' ? (
                        <>
                            <label className="label-text">{t('modalLevel.targetDomainLabel')}</label>
                            <div className="access-btn-group" id="newModuleAccessGroup">
                                {DOMAIN_OPTIONS.map((domain) => (
                                    <button
                                        key={domain}
                                        type="button"
                                        className={`access-btn${selectedLevelDomains.includes(domain) ? ' active' : ''}`}
                                        data-val={domain}
                                        onClick={() => toggleAccessBtn(domain)}
                                    >
                                        {t(`modalLevel.domains.${domain}`)}
                                    </button>
                                ))}
                            </div>
                        </>

                    ) : (
                        <>
                            <div id="editModuleDraftContainer" style={{ display: 'block', marginBottom: '14px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem', fontWeight: '600', cursor: 'pointer', marginBottom: '8px' }}>
                                    <input type="checkbox" id="editModuleIsDraft" defaultChecked={selectedLevel?.is_draft || false} style={{ width: '16px', height: '16px', accentColor: 'var(--accent)' }} onChange={(e) => setLevelIsDraft(e.target.checked)} />
                                    {t('modalLevel.draftCheckboxLabel')}
                                </label>
                                {levelIsDraft && (
                                    <select id="editModuleTargetSelect" className="login-input" value={draftTargetId} onChange={(e) => setDraftTargetId(e.target.value)} style={{ display: 'flex', padding: '8px', overflowY: 'auto', maxHeight: '100px' }}>
                                        <option value="">{t('modalLevel.selectTargetModulePlaceholder')}</option>
                                        {renderDraftTargetOptions()}
                                    </select>
                                )}
                            </div>

                            <div id="editModuleAdminAccessContainer" style={{ display: 'flex', flexDirection: 'column', marginBottom: '14px' }}>
                                <label className="label-text">{t('modalLevel.adminAccessLabel')} <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 'normal' }}>{t('modalLevel.adminAccessHint')}</span></label>
                                <div className="access-btn-group" id="editModuleAdminAccessGroup" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                    {renderAdminAccessCheckboxes()}
                                </div>
                            </div>
                        </>
                    )

                    }

                    <label className="label-text" htmlFor="editModuleImage">{t('modalLevel.moduleCoverageImageLabel')}</label>
                    <input
                        type="file"
                        id="editModuleImage"
                        accept="image/*"
                        className="login-input"
                        style={{ paddingTop: '9px' }}
                        onChange={handleEditIconChange}
                    />
                    {editModuleIconPreview && !removeEditIcon && (
                        <div className="module-image-preview">
                            <img src={editModuleIconPreview} alt="Current module preview" />
                        </div>
                    )}
                    {selectedLevel?.icon_url && !editModuleIcon && !removeEditIcon && (
                        <button
                            type="button"
                            className="btn-primary btn-secondary"
                            style={{ marginTop: '10px' }}
                            onClick={() => {
                                setEditModuleIcon(null);
                                setEditModuleIconPreview('');
                                setRemoveEditIcon(true);
                            }}
                        >
                            {t('modalLevel.removeImage')}
                        </button>
                    )}
                    <div className="modal-buttons">
                        <button className="btn-primary" onClick={() => handleUpdateLevel(selectedLevel.id)} disabled={isSubmitting}>
                            {t('common.change')}
                        </button>
                        <button
                            className="btn-primary btn-secondary"
                            onClick={closeModal}
                        >
                            {t('common.cancel')}
                        </button>
                    </div>
                </div>
            ) : Action === 'Delete' ? (
                <div className="modal-box">
                    <h2>{t('common.confirmDeletion')}</h2>
                    <p>{t('modalLevel.deleteLevelConfirm')}</p>
                    <div className="modal-buttons">
                        <button
                            className="btn-primary btn-delete"
                            onClick={() => handleDeleteLevel(selectedLevel.id)}
                            disabled={isSubmitting}
                        >
                            {t('common.delete')}
                        </button>
                        <button
                            className="btn-primary btn-secondary"
                            onClick={closeModal}
                        >
                            {t('common.cancel')}
                        </button>
                    </div>
                </div>
            ) : null}
        </div>
        </>
    );
}

export default ModalLevel;